import React from "react";
import { CHUNK_RELOAD_KEY } from "./lazyWithRetry";

const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const msg = String(error.message || "");
  return (
    /Loading chunk [^\s]+ failed/.test(msg) ||
    /Loading CSS chunk [^\s]+ failed/.test(msg) ||
    /Failed to fetch dynamically imported module/.test(msg) ||
    /error loading dynamically imported module/.test(msg)
  );
};

/* eslint-disable react/prop-types */
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // TODO(observability): forward to a server-side log endpoint once one
    // exists. For now console.error is the only sink — kept on purpose so
    // failures are still visible in user devtools / Sentry-style integrations.
    // Include route + chunk context so recurring crashes are diagnosable from
    // a single console line (route is the strongest signal for which page broke).
    let route = "";
    try {
      route = window.location.pathname + window.location.search;
    } catch {
      /* ignore */
    }
    console.error(
      `[RouteErrorBoundary] route=${route} chunk=${isChunkLoadError(error)}`,
      error,
      info?.componentStack || info
    );
  }

  handleReload = () => {
    try {
      window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);
    const title = chunk ? "New version available" : "Something went wrong";
    const body = chunk
      ? "OfferYard has been updated. Please reload the page to load the latest version."
      : "We hit an unexpected error. You can retry or go back to the home page.";

    return (
      <div
        role="alert"
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "32px 16px",
          gap: "12px",
        }}
      >
        <h2 style={{ margin: 0 }}>{title}</h2>
        <p style={{ maxWidth: 520, color: "#555", margin: 0 }}>{body}</p>
        <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "10px 20px",
              borderRadius: 6,
              border: "1px solid #d4a900",
              background: "#FFC107",
              color: "#1a1a1a",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Reload page
          </button>
          {!chunk && (
            <button
              type="button"
              onClick={this.handleRetry}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: "1px solid #ccc",
                background: "#fff",
                color: "#1a1a1a",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Try again
            </button>
          )}
          <a
            href="/"
            style={{
              padding: "10px 20px",
              borderRadius: 6,
              border: "1px solid #ccc",
              color: "#1a1a1a",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            Go home
          </a>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
